import { Doughnut } from "react-chartjs-2";
import { useMemo, useState, useEffect } from "react";
import { getInfluxdbData } from "../../../services/api/influxdb-tools";
import { useOrgData } from "../../../contexts/OrgDataContext";
import { InformationCircleIcon } from "@heroicons/react/20/solid";
import { InputSelect } from "../../../components/forms/inputs/InputSelect";
import { formatSecondsToFullDate, getCurrentTimestamp } from "../../../helpers/date/date-helper";
import { useErgonomicsContext } from "../../../contexts/ErgonomicsContext";
import { Panel } from "../../../components/panels/Panel";


export const ErgonomicsFatigueIndex = ({ showWorkstationSelector = false, forceLastDay = false, showPanel = false }) => {

  const { workstations, workstationSelected } = useOrgData();
  const [localWorkstationSelected, setLocalWorkstationSelected] = useState(null);
  const { selectedDateRange } = useErgonomicsContext();
  const [index, setIndex] = useState(0);

  useEffect(() => {
    setLocalWorkstationSelected(workstationSelected);
  }, [workstationSelected]);

  const stopDate = forceLastDay ? getCurrentTimestamp() : Math.floor(selectedDateRange.stop / 1000); // timestamp in ms
  const startDate = stopDate - 30 * 86400; // timestamp in ms

  useEffect(() => {
    if (localWorkstationSelected === null) return;

    const getData = async () => {
      let data;
      data = await getInfluxdbData('engine', 'results',
        [['client_id', '==', localWorkstationSelected?.device?.stream_id]], ['reba_fatigue_index_daily'],
        startDate, stopDate, null,null, 'last');
      if (data.length) setIndex(data[0].value);
    };

    getData();
  }, [localWorkstationSelected]);

  let fillColor;
  if (index >= 80) fillColor = "rgba(75, 255, 192, 0.8)";   // Green
  else if (index >= 60) fillColor = "rgba(255, 206, 86, 0.8)"; // Yellow
  else if (index >= 40) fillColor = "rgba(255, 150, 86, 0.8)"; // Orange
  else if (index >= 20) fillColor = "rgba(255, 99, 132, 0.8)"; // Red
  else fillColor = "rgba(255, 120, 150, 0.9)";                // Bright red

  const data = useMemo(
    () => ({
      labels: ['', ''],
      datasets: [
        {
          data: [index, 100 - index],
          backgroundColor: [fillColor, "#333333"],
          borderWidth: 0,
        },
      ],
    }),
    [index, fillColor]
  );

  const options = useMemo(
    () => ({
      cutout: "70%",
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false },
        centerText: {
          text: String(index), // <-- affiche la valeur brute
          color: "#FFFFFF",
        },
      },
    }),
    [index]
  );

  const centerTextPlugin = {
    id: "centerText",
    afterDraw: (chart, _args, opts) => {
      const { width, height, ctx } = chart;
      const text = opts.text ?? "";

      ctx.save();
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillStyle = opts.color || "#fff";

      const fontSize = Math.min(height / 5, 32);
      ctx.font = `${fontSize}px sans-serif`;

      ctx.fillText(text + '/100', width / 2, height / 2);
      ctx.restore();
    },
  };

  const ConditionalWrapper = ({ condition, wrapper, children }) => condition ? wrapper(children) : children;

  return (
    <ConditionalWrapper
      condition={showPanel}
      wrapper={children =>
        <Panel title="INDICE DE FATIGUE GENERALE D'HIER" noPadding={true} subTitle={formatSecondsToFullDate(stopDate - 86400)}>
          {children}
        </Panel>
      }
    >
      <div className="h-full flex flex-col gap-4 p-4 overflow-x-hidden">
        {
          showWorkstationSelector &&
            <div className="w-64">
              <InputSelect
                items={workstations}
                onChange={setLocalWorkstationSelected}
                itemToString={(item) => item?.name}
                defaultValue={localWorkstationSelected}
              />
            </div>
        }
        <div className="flex-1 min-h-0 py-4">
          <Doughnut data={data} options={options} plugins={[centerTextPlugin]} />
        </div>
        <div className="max-h-16 flex text-sm text-perception-gray-600 mb-4">
          <InformationCircleIcon aria-hidden="true" className="min-w-5 size-5 mr-2" />
          Indicateur débutant à 100 et diminuant selon la moyenne du score REBA. Chaque catégorie de risque applique une pénalité croissante (0, 2, 5, 10, 15 pts/h), traduisant l’accumulation de fatigue.
        </div>
      </div>
    </ConditionalWrapper>
  );
};